import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useState } from 'react';
import { SchoolFactsDisclaimer } from '../../school-page/components/SchoolPageTabContentOverview/subcomponents/SchoolFactsBrick/subcomponents/SchoolFactsDisclaimer/SchoolFactsDisclaimer';
import { NearbySchoolsDisclaimer } from '../../school-page/components/SchoolPageTabContentOverview/subcomponents/NearbySchoolsBrick/subcomponents/NearbySchoolsDisclaimer/NearbySchoolsDisclaimer';
import { SchoolCatchmentDisclaimer } from '../../school-page/components/SchoolPageTabContentOverview/subcomponents/SchoolCatchmentDisclaimerBrick/subcomponents/SchoolCatchmentDisclaimer/SchoolCatchmentDisclaimer';

interface DisclaimerModal {
  name: 'schoolCatchmentDisclaimer' | 'nearbySchoolsDisclaimer' | 'schoolFactsDisclaimer';
}

type Modal = DisclaimerModal | null;

interface ModalContext {
  displayModal: (x: Modal) => void;
}

const ModalContext = createContext<ModalContext | undefined>(undefined);

export const useModalContext = (): ModalContext => {
  const context = useContext(ModalContext);
  if (context === undefined) {
    throw new Error('ModalContext must be used within a ModalContext.Provider');
  }
  return context;
};

interface ModalHandler {
  modal: Modal;
  onClose: () => void;
}

export const ModalHandler: FunctionComponent<ModalHandler> = ({ modal, onClose }) => {
  switch (modal?.name) {
    case 'schoolCatchmentDisclaimer':
      return <SchoolCatchmentDisclaimer opened onClose={onClose} />;
    case 'nearbySchoolsDisclaimer':
      return <NearbySchoolsDisclaimer opened onClose={onClose} />;
    case 'schoolFactsDisclaimer':
      return <SchoolFactsDisclaimer opened onClose={onClose} />;
    default:
      return null;
  }
};

export const ModalContextProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const [modal, setModal] = useState<Modal>(null);
  return (
    <ModalContext.Provider value={{ displayModal: setModal }}>
      {modal && <ModalHandler modal={modal} onClose={() => setModal(null)} />}
      {children}
    </ModalContext.Provider>
  );
};
